import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, Package, Loader2, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PAYSTACK_PUBLIC_KEY, isPaystackConfigured } from '../../config/paystack';

interface FEMRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AMOUNT_NGN = 60000;

const FEMRegistrationModal: React.FC<FEMRegistrationModalProps> = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    products: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (window.PaystackPop) {
      setReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setReady(true);
    script.onerror = () => setError('Failed to load payment processor. Please refresh.');
    document.body.appendChild(script);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPaystackConfigured()) {
      setError('Payment is not configured. Please contact support.');
      return;
    }
    if (!window.PaystackPop) {
      setError('Payment processor is still loading. Please wait a moment.');
      return;
    }

    setLoading(true);
    try {
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: form.email,
        amount: AMOUNT_NGN * 100,
        currency: 'NGN',
        metadata: {
          custom_fields: [
            { display_name: 'Full Name', variable_name: 'full_name', value: form.fullName },
            { display_name: 'Phone', variable_name: 'phone', value: form.phone },
            { display_name: 'Location', variable_name: 'location', value: form.location },
            { display_name: 'Products of Interest', variable_name: 'products', value: form.products },
            { display_name: 'Program', variable_name: 'program', value: 'Foodstuff Export Masterclass' },
          ],
        },
        callback: () => {
          setLoading(false);
          onClose();
        },
        onClose: () => setLoading(false),
      });
      handler.openIframe();
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-700 px-6 py-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/15 rounded-lg">
                  <Package className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Foodstuff Export Masterclass</h3>
                  <p className="text-sm text-green-100">Secure your seat instantly</p>
                </div>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-bold">₦{AMOUNT_NGN.toLocaleString()}</span>
                <span className="text-sm text-green-100">/ participant</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Field icon={User} label="Full Name" value={form.fullName}
                onChange={(v) => setForm({ ...form, fullName: v })} placeholder="e.g. Ada Okafor" required />
              <Field icon={Mail} label="Email Address" type="email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })} placeholder="you@example.com" required />
              <Field icon={Phone} label="Phone Number" type="tel" value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })} placeholder="+234 800 000 0000" required />
              <Field label="Location (City, Country)" value={form.location}
                onChange={(v) => setForm({ ...form, location: v })} placeholder="Lagos, Nigeria" required />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Products you want to export
                </label>
                <textarea
                  required
                  rows={2}
                  value={form.products}
                  onChange={(e) => setForm({ ...form, products: e.target.value })}
                  placeholder="e.g. garri, hibiscus, sesame seeds"
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !ready}
                className="w-full py-3.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Processing…</>
                ) : (
                  <>Pay ₦{AMOUNT_NGN.toLocaleString()} with Paystack</>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 pt-1">
                <ShieldCheck className="h-4 w-4 text-green-600" />
                Secured by Paystack · SSL encrypted
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

const Field: React.FC<{
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ icon: Icon, label, type = 'text', value, onChange, placeholder, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />}
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm ${Icon ? 'pl-10' : ''}`}
      />
    </div>
  </div>
);

export default FEMRegistrationModal;